import { AlertTriangle, Bell, Check, Clock, X } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import Badge from "@/components/ui/badge";
import { cn } from "@/utils/cn";
import { formatCompactTimeAgo } from "@/utils/dateUtils";
import type { NotificationStatus } from "@/contexts/NotificationsProvider";
import type { Notification } from "@/pages/notifications/types";

const STATUS_STYLES: Record<
  NotificationStatus,
  { badgeClass: string; Icon: LucideIcon }
> = {
  accepted: {
    badgeClass: "bg-status-success-bg text-status-success-text",
    Icon: Check,
  },
  pending: {
    badgeClass: "bg-status-warning-bg text-status-warning-text",
    Icon: Clock,
  },
  declined: {
    badgeClass: "bg-status-error-bg text-status-error-text",
    Icon: X,
  },
  // Distinct from "declined" — a failed fetch, not a recorded decision.
  error: {
    badgeClass: "bg-status-error-bg text-status-error-text",
    Icon: AlertTriangle,
  },
  unclassified: {
    badgeClass: "bg-surface-muted text-text-muted",
    Icon: Bell,
  },
};

export interface NotificationListItemProps {
  notification: Notification;
  status: NotificationStatus;
  onMarkRead: (n_id: string) => void;
  className?: string;
}

function NotificationListItem({
  notification: n,
  status,
  onMarkRead,
  className = "",
}: NotificationListItemProps) {
  const isUnseen = !n.seen_on;
  const relativeTime = n.created_on ? formatCompactTimeAgo(n.created_on) : "";
  const { badgeClass, Icon } = STATUS_STYLES[status];
  const handleMarkRead = () => onMarkRead(n.n_id);

  return (
    <div
      className={cn(
        "relative flex gap-3 px-4 py-3 transition-colors hover:bg-surface-muted",
        className
      )}
    >
      {/* Clickable overlay to mark as read — a sibling of the action <a> tags below,
          not an ancestor, so it doesn't nest interactive elements inside a role="button". */}
      <button
        type="button"
        onClick={handleMarkRead}
        aria-label={`Mark as read: ${n.message?.header ?? "notification"}`}
        className="absolute inset-0 h-full w-full cursor-pointer border-0 bg-transparent outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40"
      />

      <div
        className={cn(
          "relative pointer-events-none flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
          badgeClass
        )}
      >
        <Icon size={15} />
      </div>

      <div className="relative pointer-events-none min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          {n.message?.header && (
            <p
              className={cn(
                "text-[13.5px] leading-snug",
                isUnseen
                  ? "font-bold text-text-heading"
                  : "font-semibold text-text-primary"
              )}
            >
              {n.message.header}
            </p>
          )}
          {isUnseen && <Badge variant="dot" className="mt-1.5 shrink-0" />}
        </div>

        {n.message?.text && (
          <p className="mt-0.5 text-[12.5px] leading-snug text-text-secondary">
            {n.message.text}
          </p>
        )}
        {n.message?.subtext?.text && (
          <p className="mt-0.5 text-xs text-text-muted">
            {n.message.subtext.text}
          </p>
        )}

        {n.message?.actions && n.message.actions.length > 0 && (
          <div className="mt-2 flex gap-2 pointer-events-auto">
            {n.message.actions.map((action) => (
              <a
                key={action.name}
                href={action.url}
                target={action.open_in_new_tab ? "_blank" : undefined}
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="rounded-md bg-primary-light px-2.5 py-1 text-[11px] font-semibold text-primary hover:bg-primary-medium"
              >
                {action.name}
              </a>
            ))}
          </div>
        )}

        <p className="mt-1.5 text-[11px] text-text-muted">{relativeTime}</p>
      </div>
    </div>
  );
}

export default NotificationListItem;
